import React from 'react';
import { ColumnsType } from 'antd/es/table';
import { EditableColumnType, WorkaroundDefinedTypeEnum } from './types';
import { Progress } from 'antd';
import { renderComplianceBadges } from '../../../utils/complianceBadgeRenderer';
import { WorkaroundDefinedType } from '../../../pages/workarounds/types';

import { Workaround } from '../../process-areas-and-processes-workarounds/types';
import { CollapsibleText } from '../../../../../components/generic/TextTruncation';
import { CompletionPercentage } from '../../../components';

export const commonColumns: EditableColumnType[] = [
  {
    title: 'Is Workaround Needed',
    dataIndex: 'isWorkaroundNeeded',
    key: 'isWorkaroundNeeded',
    align: 'center',
    editable: true,
    type: 'text',
    render: (value: boolean) => (value ? 'Yes' : 'No'),
  },
  {
    title: (
      <span>
        Workaround <br />
        Timeline Compliance
      </span>
    ),
    dataIndex: 'workaroundTimelineCompliance',
    key: 'workaroundTimelineCompliance',
    align: 'center',
    width: 250,
    editable: false,
    render: (complianceData, workaroundRecord) => {
      return renderComplianceBadges(complianceData, {
        isWorkaroundNeeded: workaroundRecord.isWorkaroundNeeded,
      });
    },
  },
  {
    title: 'Workaround Defined',
    dataIndex: 'workaroundDefined',
    key: 'workaroundDefined',
    editable: true,
    type: 'text',
    align: 'center',
    options: [
      {
        value: WorkaroundDefinedTypeEnum.NO_WORKAROUND,
        label: 'No Workaround',
      },
      { value: WorkaroundDefinedTypeEnum.INFINITE, label: 'Infinite' },
      { value: WorkaroundDefinedTypeEnum.DEFINED_TIME, label: 'Defined Time' },
    ],
    render: (value: string) => {
      const option = [
        {
          value: WorkaroundDefinedTypeEnum.NO_WORKAROUND,
          label: 'No Workaround',
        },
        { value: WorkaroundDefinedTypeEnum.INFINITE, label: 'Infinite' },
        {
          value: WorkaroundDefinedTypeEnum.DEFINED_TIME,
          label: 'Defined Time',
        },
      ].find((opt: { value: string; label: string }) => opt.value === value);
      return option ? option.label : value;
    },
  },
  {
    title: 'Runtime with Workaround',
    dataIndex: 'runtimeHours',
    key: 'runtimeHours',
    align: 'center',
    editable: true,
    type: 'number',
    suffix: 'hours',
    render: (runtimeHours: number | null, record: Workaround) => {
      // Infinite workaround overrides any runtime hours value
      if (record.workaroundDefined === WorkaroundDefinedTypeEnum.INFINITE) {
        return <span>Infinite</span>;
      }

      // Handle null or undefined runtime hours
      if (!runtimeHours) {
        return <span>{WorkaroundDefinedType.NO_WORKAROUND}</span>;
      }

      // Handle maximum value (treated as N/A)
      if (runtimeHours === Number.MAX_VALUE) {
        return <span className="text-[#BABABA]">N/A</span>;
      }

      // Handle normal runtime hours
      return <span>{runtimeHours} hours</span>;
    },
  },
  {
    title: 'Maximum Allowable Data Loss',
    dataIndex: 'maxDataLossHours',
    key: 'maxDataLossHours',
    align: 'center',
    editable: true,
    type: 'number',
    suffix: 'hours',
    render: (value: number) =>
      value ? (
        <span>{value} hours</span>
      ) : (
        <span className="text-[#BABABA]">N/A</span>
      ),
  },
  {
    title: 'Workaround Description',
    dataIndex: 'workaroundDescription',
    key: 'workaroundDescription',
    editable: true,
    type: 'textarea',
    align: 'left',
    render: (text: string) => (
      <CollapsibleText text={text} fallbackText="N/A" />
    ),
  },
];

export const overviewColumns: ColumnsType<any> = [
  {
    title: '# of Applications Tagged',
    dataIndex: 'totalTaggedApplications',
    key: 'totalTaggedApplications',
    align: 'center',
    sorter: (a: any, b: any) =>
      a.totalTaggedApplications - b.totalTaggedApplications,
  },
  {
    title: 'Workaround Completion',
    key: 'completionPercentage',
    sorter: (a: any, b: any) => a.completionPercentage - b.completionPercentage,
    render: (record: any) => (
      <CompletionPercentage percent={record.completionPercentage} />
    ),
  },
];
